import type { ReactNode } from "react";

export type StatusTone =
  | "success"
  | "danger"
  | "warning"
  | "info"
  | "neutral";

interface StatusBadgeProps {
  children: ReactNode;
  tone?: StatusTone;
  className?: string;
}

const TONE_CLASSES: Record<StatusTone, string> = {
  success:
    "bg-emerald-50 text-emerald-700 ring-emerald-600/20 " +
    "dark:bg-emerald-500/10 dark:text-emerald-300 dark:ring-emerald-400/25",
  danger:
    "bg-rose-50 text-rose-700 ring-rose-600/20 " +
    "dark:bg-rose-500/10 dark:text-rose-300 dark:ring-rose-400/25",
  warning:
    "bg-amber-50 text-amber-700 ring-amber-600/20 " +
    "dark:bg-amber-500/10 dark:text-amber-300 dark:ring-amber-400/25",
  info:
    "bg-sky-50 text-sky-700 ring-sky-600/20 " +
    "dark:bg-sky-500/10 dark:text-sky-300 dark:ring-sky-400/25",
  neutral:
    "bg-gray-50 text-secondary ring-gray-300/70 " +
    "dark:bg-gray-800/60 dark:text-primary dark:ring-gray-700",
};

const DOT_CLASSES: Record<StatusTone, string> = {
  success: "bg-emerald-500 dark:bg-emerald-400",
  danger: "bg-rose-500 dark:bg-rose-400",
  warning: "bg-amber-500 dark:bg-amber-400",
  info: "bg-sky-500 dark:bg-sky-400",
  neutral: "bg-gray-400 dark:bg-gray-500",
};

export default function StatusBadge({
  children,
  tone = "neutral",
  className = "",
}: StatusBadgeProps) {
  return (
    <span
      className={[
        "inline-flex items-center gap-1.5",
        "px-2 py-0.5 rounded-md",
        "ring-1 ring-inset",
        "text-2xs font-semibold uppercase tracking-wider",
        "leading-5 whitespace-nowrap",
        TONE_CLASSES[tone],
        className,
      ].join(" ")}
    >
      <span
        className={`w-1.5 h-1.5 rounded-full ${DOT_CLASSES[tone]}`}
        aria-hidden="true"
      />
      {children}
    </span>
  );
}
